import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl, logError } from 'src/utils/ApiHelper';
import { CSA, SUCCESS_MESSAGES } from '../../CsaCoordination/contants';
import {
  IAddCommentPayload,
  ICreateTicket,
  IFetchRequestPayload,
  IRequestForm
} from '../../CsaCoordination/types';
import { IOption } from '../../ExternalDashboardSensei/types';
import {
  setLoadingRequestRaised,
  setLoadingRequestReceived,
  setLoadingTicket,
  setRequestRaised,
  setRequestReceived,
  setTicketData
} from '../reducers/CsaTicketingSlice';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';

export const fetchRequestRaisedTickets =
  (payload: IFetchRequestPayload) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.GET_CSA_TICKETS);
    dispatch(setLoadingRequestRaised(true));
    axiosInstance
      .post(url, {
        filters: {
          REQUESTERS: [payload?.userId],
          LOAN_ACCOUNT_NUMBERS: [payload?.loanId]
        }
      })
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          const data = response?.data?.data;
          dispatch(setRequestRaised(data));
        }
      })
      .catch(err => {
        logError(err);
      })
      .finally(() => {
        dispatch(setLoadingRequestRaised(false));
      });
  };

export const fetchRequestReceivedTickets =
  (payload: IFetchRequestPayload) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.GET_CSA_TICKETS);
    dispatch(setLoadingRequestReceived(true));
    axiosInstance
      .post(url, {
        filters: {
          ASSIGNEES: [payload?.userId],
          LOAN_ACCOUNT_NUMBERS: [payload?.loanId]
        }
      })
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          const data = response?.data?.data;
          dispatch(setRequestReceived(data));
        }
      })
      .catch(err => {
        logError(err);
      })
      .finally(() => {
        dispatch(setLoadingRequestReceived(false));
      });
  };

export const fetchSingleTicket = (ticketReferenceId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_CSA_SINGLE_TICKET, { ticketReferenceId });
  dispatch(setTicketData({}));
  dispatch(setLoadingTicket(true));
  axiosInstance
    .get(url)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        dispatch(setTicketData(response?.data));
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => {
      dispatch(setLoadingTicket(false));
    });
};

export const createTicket = (
  payload: ICreateTicket,
  setLoading: (val: boolean) => void,
  callbackFn: () => void
) => {
  const url = getApiUrl(ApiKeys.CREATE_TICKET);
  setLoading(true);
  axiosInstance
    .post(url, payload)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        toast(SUCCESS_MESSAGES.TICKET_CREATED, { type: 'success' });
        callbackFn?.();
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CREATE_TASK_SUCCESSFUL, {
          taskType: payload.supportRequestType
        });
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => {
      setLoading(false);
    });
};

export const fetchFormOptions = (
  loanNo: string,
  setLoading: (val: boolean) => void,
  callbackFn: (values: IOption[], subTitle: string) => void
) => {
  const url = getApiUrl(ApiKeys.GET_FORM_OPTIONS);
  setLoading(true);
  axiosInstance
    .get(url, {
      params: {
        loanAccountNumber: loanNo,
        supportRequestUserType: CSA
      }
    })
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        const requestForm = response?.data?.requestForm || [];
        const requestTo = response?.data?.requestTo || {};
        const convertedData = requestForm.map((item: IRequestForm) => {
          const [value, label] = Object.entries(item)[0];
          return { label, value };
        });
        callbackFn?.(convertedData, requestTo?.name);
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => {
      setLoading(false);
    });
};

export const acknowledgeTicket = (ticketReferenceId: string, callbackFn?: () => void) => {
  const url = getApiUrl(ApiKeys.ACKNOWLEDGE_TICKET, { ticketReferenceId });
  axiosInstance
    .patch(url, {}, { params: { supportRequestUserType: CSA } })
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        callbackFn?.();
      }
    })
    .catch(err => {
      logError(err);
    });
};

export const addComment = (
  ticketReferenceId: string,
  payload: IAddCommentPayload,
  callbackFn: () => void,
  finallyCallBackFn: () => void
) => {
  const url = getApiUrl(ApiKeys.ADD_COMMENT, { ticketReferenceId });
  axiosInstance
    .post(url, payload)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        toast(SUCCESS_MESSAGES.COMMENT_ADDED, { type: 'success' });
        callbackFn?.();
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_TASK_COMMENT_ADDED, {
          ticketId: ticketReferenceId
        });
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => finallyCallBackFn());
};
